<template>
  <section>
    <article v-if="!hasEventToday" :class="[$style.month, $style.grid]">
      <h2>
        <time
          :class="$style.date"
          :datetime="`${firstDateObj.isoDate} 00:00:00`"
          v-html="`${firstDateObj.month} ${firstDateObj.year}`"
        ></time>
      </h2>
      <h3>
        <time :datetime="`${firstDateObj.isoDate} 00:00:00`">
          {{ firstDateObj.weekday }} {{ firstDateObj.day }}
        </time>
      </h3>
      <header :class="$style.event">
        <p>There are no events scheduled for this day.</p>
      </header>
    </article>
    <article
      v-for="(event, key) in events"
      :key="key"
      :class="[{ [$style.day]: isNewDate(key), [$style.month]: isNewMonth(key) }, $style.grid]"
    >
      <h2 v-if="isNewMonth(key)">
        <time :class="$style.date" :datetime="dateTime(event.start)">
          {{ `${event.start.month} ${event.start.year}` }}
        </time>
      </h2>
      <h3 v-if="key === 0 || isNewDate(key)">
        <time :datetime="dateTime(event.start)">
          {{ event.start.weekday }} {{ event.start.day }}
        </time>
      </h3>
      <header :class="$style.event">
        <router-link
          :class="$style.title"
          :to="{
            name: 'CalendarEvent',
            params: { date: event.start.isoDate, slug: event.eventSlug },
          }"
          :ga4-event="
            JSON.stringify({
              click_type: 'title',
              component: 'CalendarMonth',
              content_group: 'calendar',
              gtm_tag: 'link',
              link_text: `${event.title}`,
              link_url: `${event.eventSlug}`,
            })
          "
        >
          <h4 v-html="event.title"></h4>
        </router-link>
        <time v-if="!event.hideStartTime" :class="$style.time" :datetime="dateTime(event.start)">
          {{ `${formatTime(event.start)} ${event.timezoneAbbr}` }}
        </time>
        <p v-if="event.specificText && event.hideStartTime">{{ event.specificText }}</p>
        <p v-if="showDesc" v-html="event.excerpt"></p>
        <EventTypes
          v-if="event.eventTypes && Object.keys(event.eventTypes.nodes).length"
          :types="event.eventTypes.nodes"
        ></EventTypes>
      </header>
    </article>
  </section>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent } from 'vue';
import { EventInterface, EventDateInterface } from '@/types/event.interface';
import { useDateFormat } from '@/composables/Common.js';

const EventTypes = defineAsyncComponent(() => import('@/components/calendar/EventTypes.vue'));

const props = withDefaults(
  defineProps<{
    events: EventInterface[];
    firstDate: Date;
    showDesc?: boolean;
  }>(),
  { showDesc: false },
);

const { dateTime, formatTime } = useDateFormat();

const firstDateObj = computed(() => {
  const month = new Intl.DateTimeFormat('en-US', { month: 'long' }).format(props.firstDate);
  const weekday = new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(props.firstDate);

  return {
    day: props.firstDate.getDate().toString(),
    isoDate: props.firstDate.toISOString().split('T')[0],
    month,
    weekday,
    year: props.firstDate.getFullYear().toString(),
  };
});
const hasEventToday = computed(() => {
  return firstDateObj.value.isoDate === props.events[0].start.isoDate;
});

function isNewDate(key: number): boolean {
  return (
    (key === 0 && !hasEventToday.value) ||
    (key > 0 && props.events[key].start.isoDate !== props.events[key - 1].start.isoDate)
  );
}

function isNewMonth(key: number): boolean {
  if (key === 0) {
    // Has event the day selected.
    if (isTheSameDay(firstDateObj.value, props.events[0].start)) {
      return true;
    }

    // Does not have an event on the selected day.
    if (!isTheSameDay(firstDateObj.value, props.events[0].start)) {
      // Display new month only if the first event is a different month than the selected date.
      return firstDateObj.value.month !== props.events[0].start.month;
    }
  }

  return props.events[key].start.month !== props.events[key - 1].start.month;
}

function isTheSameDay(today: EventDateInterface, tomorrow: EventDateInterface): boolean {
  return today.day === tomorrow.day;
}
</script>

<style lang="scss" module>
.date {
  display: block;
}

.event {
  @include space-24-32-above;

  :global(.tags) {
    @include space-0-above;
  }

  time {
    display: block;
  }
}

.month {
  margin: 4rem 0 0 0;

  h2,
  h3 {
    @include space-24-32-above;
  }
}

.time {
  @include subtitle-2;
  @include space-0-above;
}

.title {
  display: inline-block;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  h4 {
    margin-top: 0;
    width: fit-content;
  }
}

@media (min-width: $break-point) {
  .event {
    grid-column: 5 / span 8;
  }

  .grid {
    @include grid;

    h2 {
      grid-column: span 12;
    }

    h3 {
      grid-column: span 4;
    }
  }
}
</style>
